import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { clientAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { KENYA_COUNTIES, formatPlace } from '../../data/kenyaLocations';
import {
  MagnifyingGlassIcon,
  MapPinIcon,
  HeartIcon,
  BuildingStorefrontIcon,
  StarIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';

const ITEMS_PER_PAGE = 10;

const ClientExplore = () => {
  const [loading, setLoading] = useState(true);
  const [farms, setFarms] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [filter, setFilter] = useState({ county: '', search: '' });
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    loadData();
    setCurrentPage(1);
  }, [filter.county]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter.county) params.county = filter.county;
      
      const [shopsRes, favsRes] = await Promise.all([
        clientAPI.listShops(params),
        clientAPI.getFavorites()
      ]);
      
      setFarms(shopsRes.data.shops || []);
      setFavorites((favsRes.data.favorites || []).map(f => f.farmId));
    } catch (error) {
      console.error('Load farms error:', error);
      toast.error('Failed to load farms');
    } finally {
      setLoading(false);
    }
  };

  const filteredFarms = useMemo(() => {
    let result = farms;
    if (filter.search) {
      const q = filter.search.toLowerCase();
      result = result.filter(farm =>
        farm.name?.toLowerCase().includes(q) ||
        farm.description?.toLowerCase().includes(q) ||
        farm.county?.toLowerCase().includes(q) ||
        farm.town?.toLowerCase().includes(q) ||
        farm.location?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [farms, filter.search]);

  // Pagination
  const totalPages = Math.ceil(filteredFarms.length / ITEMS_PER_PAGE);
  const paginatedFarms = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredFarms.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredFarms, currentPage]);

  const toggleFavorite = async (farmId) => {
    try {
      if (favorites.includes(farmId)) {
        await clientAPI.removeFavorite(farmId);
        setFavorites(prev => prev.filter(id => id !== farmId));
        toast.success('Removed from favorites');
      } else {
        await clientAPI.addFavorite(farmId);
        setFavorites(prev => [...prev, farmId]);
        toast.success('Added to favorites');
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 rounded-2xl p-6 sm:p-8 text-white">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 backdrop-blur rounded-xl">
              <BuildingStorefrontIcon className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Explore Farms</h1>
              <p className="text-amber-100">Find farms near you selling fresh products</p>
            </div>
          </div>
        </div>
        
        {/* Stats */}
        <div className="relative mt-6 grid grid-cols-2 gap-3">
          <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{farms.length}</p>
            <p className="text-amber-200 text-xs">Farms Available</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{favorites.length}</p>
            <p className="text-amber-200 text-xs">Your Favorites</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search farms..."
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
              value={filter.search}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            />
          </div>
          <select
            className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
            value={filter.county}
            onChange={(e) => setFilter({ ...filter, county: e.target.value })}
          >
            <option value="">All Counties</option>
            {KENYA_COUNTIES.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Farms Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : filteredFarms.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center border border-gray-100">
          <BuildingStorefrontIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No farms found</h3>
          <p className="text-gray-500">Try adjusting your search or location filter</p>
        </div>
      ) : (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {paginatedFarms.map((farm) => (
            <div key={farm.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow">
              {/* Farm Image/Header */}
              <div className="h-40 bg-gradient-to-br from-amber-100 to-orange-100 relative">
                {(farm.imageUrl || farm.logoUrl) ? (
                  <img
                    src={farm.imageUrl || farm.logoUrl}
                    alt={farm.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <BuildingStorefrontIcon className="h-16 w-16 text-amber-300" />
                  </div>
                )}
                
                {/* Favorite Button */}
                <button
                  onClick={(e) => { e.preventDefault(); toggleFavorite(farm.id); }}
                  className="absolute top-3 right-3 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-sm hover:scale-110 transition-transform"
                >
                  {favorites.includes(farm.id) ? (
                    <HeartSolidIcon className="h-5 w-5 text-red-500" />
                  ) : (
                    <HeartIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>

              {/* Farm Info */}
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 text-lg mb-1">{farm.name}</h3>
                
                {(farm.county || farm.town || farm.location) && (
                  <p className="text-sm text-gray-500 flex items-center mb-2">
                    <MapPinIcon className="h-4 w-4 mr-1" />
                    {formatPlace(farm)}
                  </p>
                )}
                
                {farm.description && (
                  <p className="text-sm text-gray-600 line-clamp-2 mb-3">{farm.description}</p>
                )}

                {/* Products Preview */}
                {farm.productCount > 0 && (
                  <p className="text-sm text-amber-600 mb-4">
                    {farm.productCount} products available
                  </p>
                )}

                <Link
                  to={`/shop/${farm.slug || farm.id}`}
                  className="block w-full py-2 text-center bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                >
                  View Shop
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white rounded-xl shadow-sm p-4 border border-gray-100 mt-4">
            <p className="text-sm text-gray-600">
              Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, filteredFarms.length)} of {filteredFarms.length} farms
            </p>
            <div className="flex items-center gap-1 sm:gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              {/* Desktop page numbers */}
              <div className="hidden sm:flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-10 h-10 rounded-lg text-sm font-medium ${
                        currentPage === pageNum
                          ? 'bg-amber-600 text-white'
                          : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              {/* Mobile: show current page */}
              <span className="sm:hidden px-3 py-2 text-sm bg-amber-600 text-white rounded-lg">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
        </>
      )}
    </div>
  );
};

export default ClientExplore;
